import React, { useState, useEffect } from 'react';
import { Menu, X, Github, Gitlab, Linkedin, Mail } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      setIsClosing(false);
    }, 300);
  };

  const navItems = ['Bio', 'Projects', 'Skills', 'Contact'];

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-gray-900/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0">
            <h1 className="text-2xl font-bold text-white">kustov<Gitlab className="inline-block -translate-y-[-6px]" size="14"/>pro</h1>
          </div>

          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navItems.map((item) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="text-gray-300 hover:text-white px-3 py-2 text-sm font-medium transition-colors duration-200"
                >
                  {item}
                </a>
              ))}
            </div>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <Github size={20} />
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <Linkedin size={20} />
            </a>
            <a
              href="mailto:contact@example.com"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              <Mail size={20} />
            </a>
          </div>

          <div className="md:hidden">
            <button
              onClick={() => isMobileMenuOpen ? closeMenu() : setIsMobileMenuOpen(true)}
              className="text-gray-300 hover:text-white p-2"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {(isMobileMenuOpen || isClosing) && (
          <div className={`md:hidden fixed inset-0 z-50 transition-all duration-300 ease-in-out ${isClosing ? 'opacity-0' : 'opacity-100'}`}>
            {/* Full-screen overlay with site background */}
            <div className={`fixed inset-0 bg-gradient-to-br from-blue-900 via-cyan-800 to-indigo-900 transition-opacity duration-300 ease-in-out ${isClosing ? 'opacity-0' : 'opacity-100'}`}>
              <div className="absolute inset-0 bg-black opacity-20"></div>
            </div>
            
            {/* Menu content */}
            <div className={`relative z-10 h-full flex flex-col transition-transform duration-300 ease-in-out ${isClosing ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}`}>
              <div className="flex justify-between items-center p-4">
                <h1 className="text-2xl font-bold text-white"></h1>
                <button
                  onClick={closeMenu}
                  className="text-gray-300 hover:text-white p-2 transition-transform duration-200 hover:scale-110"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="flex-1 flex items-center justify-center px-4">
                <div className="space-y-6 text-center">
                  {navItems.map((item, index) => (
                    <a
                      key={item}
                      href={`#${item.toLowerCase()}`}
                      className={`text-xl text-gray-300 hover:text-white block font-medium transition-all duration-300 hover:scale-105 ${isClosing ? 'translate-y-10 opacity-0' : 'translate-y-0 opacity-100'}`}
                      style={{
                        transitionDelay: isClosing ? '0ms' : `${index * 50}ms`
                      }}
                      onClick={closeMenu}
                    >
                      {item}
                    </a>
                  ))}
                  
                  <div 
                    className="flex items-center justify-center space-x-6 pt-8"
                    style={{
                      transitionDelay: isClosing ? '0ms' : '400ms'
                    }}
                  >
                    <a
                      href="https://github.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}
                    >
                      <Github size={24} />
                    </a>
                    <a
                      href="https://linkedin.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}
                    >
                      <Linkedin size={24} />
                    </a>
                    <a
                      href="mailto:contact@example.com"
                      className={`text-gray-400 hover:text-white transition-all duration-300 hover:scale-110 ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}
                    >
                      <Mail size={24} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
